// ─────────────────────────────────────────────────────────────────────
// Tenon · Flag-a-problem — web dialog + flagged-state banner (#810).
//
// Backend contract (#802, internal/api/task_flags_handlers.go):
//   POST /tasks/{id}/flag           { reason, note, photo_attachment_ids, notify_user_ids }
//   POST /tasks/{id}/flag/resolve   { resolution_note? }
// resolveTaskFlagRecipients() on the backend ALWAYS notifies the project
// creator + PM + company admins regardless of what the client sends —
// notify_user_ids here is purely ADDITIVE, so this dialog's "Also notify"
// field intentionally doesn't imply it's picking the full recipient list
// (a deliberate copy deviation from the task-peek-web.jsx prototype, which
// predates Joe's #802 ruling on mandatory recipients).
//
// Design spec: docs/design-briefs/task-improvements/task-peek-web.jsx
// FlagDialog + README.md §"Flagging a problem". Photos differ from the
// prototype's placeholder swatches — this picks from the task's REAL
// attachments (already uploaded via the Photos section, or added fresh
// through this dialog's own "Add" button) since the backend only accepts
// existing attachment ids, not inline image data.
// ─────────────────────────────────────────────────────────────────────

import * as React from 'react'
import { createPortal } from 'react-dom'
import { Icon, Avatar, AvatarStack } from '@/design-system/primitives'
import type { TaskFlag } from '@/lib/api'
import { formatDateWithOptions } from '../../utils/dateUtils'
import { attachmentThumbUrl } from '@/components/task/shared/attachments'

export const FLAG_REASONS = [
  'Missing materials', 'Access blocked', 'Waiting on inspection',
  'Waiting on another trade', 'Unsafe condition', 'Needs more info',
] as const

export interface FlagPersonOption {
  id: string
  name: string
  initials?: string
}

export interface FlagPhotoOption {
  id: string
  url: string
  thumbnail_url?: string
}

export interface FlagSubmitPayload {
  reason: string
  note: string
  photo_attachment_ids: number[]
  notify_user_ids: number[]
}

// ── Also-notify — lightweight multi-select chip toggle, shared web+mobile ──
// A searchable popover (like AssigneePicker) is overkill here: the backend
// already guarantees the mandatory recipients, so this list is short
// (project roster) and a flat toggle row reads faster than a dropdown.
export function NotifyPicker({ options, value, onChange }: {
  options: FlagPersonOption[]
  value: string[]
  onChange: (v: string[]) => void
}) {
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id])
  if (options.length === 0) {
    return (
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, color: 'var(--text-dim)', fontStyle: 'italic' }}>
        No one else to notify.
      </span>
    )
  }
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {options.map(p => {
        const on = value.includes(p.id)
        return (
          <button key={p.id} type="button" onClick={() => toggle(p.id)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '4px 12px 4px 4px', borderRadius: 999, cursor: 'pointer',
            background: on ? 'var(--brand-gold-quiet)' : 'var(--bg-surface)',
            border: `1.5px solid ${on ? 'var(--brand-gold)' : 'var(--border)'}`,
            fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600,
            color: on ? 'var(--text-primary)' : 'var(--text-secondary)',
          }}>
            <Avatar id={p.initials ?? p.id} size={22}/>
            {p.name.split(' ')[0]}
          </button>
        )
      })}
    </div>
  )
}

// ── FlagDialog — matches task-peek-web.jsx's FlagDialog ────────────────
export function FlagDialog({
  open, onClose, onSubmit, attachments, onAttachFile, peopleOptions, submitting,
}: {
  open: boolean
  onClose: () => void
  onSubmit: (payload: FlagSubmitPayload) => void | Promise<void>
  /** The task's real photo attachments — reactive, so a fresh upload
   *  triggered via onAttachFile shows up here and gets auto-selected. */
  attachments: FlagPhotoOption[]
  onAttachFile: () => void
  peopleOptions: FlagPersonOption[]
  submitting?: boolean
}) {
  const [reason, setReason] = React.useState<string | null>(null)
  const [note, setNote] = React.useState('')
  const [selectedPhotoIds, setSelectedPhotoIds] = React.useState<string[]>([])
  const [notify, setNotify] = React.useState<string[]>([])
  const baselineIds = React.useRef<Set<string>>(new Set())

  React.useEffect(() => {
    if (open) {
      setReason(null); setNote(''); setSelectedPhotoIds([]); setNotify([])
      baselineIds.current = new Set(attachments.map(a => a.id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Auto-select any photo freshly uploaded while the dialog is open.
  React.useEffect(() => {
    if (!open) return
    const fresh = attachments.filter(a => !baselineIds.current.has(a.id)).map(a => a.id)
    if (fresh.length) {
      setSelectedPhotoIds(prev => Array.from(new Set([...prev, ...fresh])))
      baselineIds.current = new Set(attachments.map(a => a.id))
    }
  }, [attachments, open])

  if (!open) return null

  const chip = (on: boolean): React.CSSProperties => ({
    padding: '7px 12px', borderRadius: 999, cursor: 'pointer',
    fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600,
    border: `1.5px solid ${on ? 'var(--st-error)' : 'var(--border-strong)'}`,
    background: on ? 'color-mix(in srgb, var(--st-error) 12%, transparent)' : 'transparent',
    color: on ? 'var(--st-error)' : 'var(--text-secondary)',
  })

  const handleSubmit = () => {
    if (!reason || submitting) return
    onSubmit({
      reason,
      note: note.trim(),
      photo_attachment_ids: selectedPhotoIds.map(Number),
      // Contacts (negative ids, no platform login) can't receive an
      // in-app/email notification — same convention AssigneePicker uses.
      notify_user_ids: notify.map(Number).filter(n => n > 0),
    })
  }

  return createPortal(
    <div onMouseDown={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'var(--bg-overlay)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onMouseDown={(e) => e.stopPropagation()} style={{
        width: 430, maxHeight: '86vh', overflowY: 'auto',
        background: 'var(--bg-surface)', border: '1px solid var(--border-strong)',
        borderRadius: 'var(--r-lg)', boxShadow: '0 24px 70px rgba(0,0,0,0.32)',
        fontFamily: 'var(--font-sans)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px 14px', borderBottom: '1px solid var(--border)' }}>
          <span style={{
            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'color-mix(in srgb, var(--st-error) 14%, transparent)', color: 'var(--st-error)',
          }}><Icon name="flag" size={18} sw={2}/></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-primary)' }}>Flag a problem</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
              Sets status to <strong>Blocked</strong> and notifies the PM, admins, and anyone else you pick.
            </div>
          </div>
          <button type="button" onClick={onClose} style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: 6, border: 'none',
            background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)',
          }}><Icon name="x" size={15} sw={2.2}/></button>
        </div>

        <div style={{ padding: '16px 20px 6px' }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 9, textTransform: 'uppercase', letterSpacing: 0.4 }}>What's wrong?</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {FLAG_REASONS.map(r => (
              <button key={r} type="button" onClick={() => setReason(r)} style={chip(reason === r)}>{r}</button>
            ))}
          </div>
        </div>

        <div style={{ padding: '16px 20px 6px' }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 9, textTransform: 'uppercase', letterSpacing: 0.4 }}>Describe it</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="What exactly is blocking, where, and what's needed to clear it…"
            style={{
              width: '100%', minHeight: 74, padding: '10px 12px', resize: 'vertical',
              background: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
              outline: 'none', fontFamily: 'var(--font-sans)', fontSize: 13.5, lineHeight: 1.5,
              color: 'var(--text-primary)', boxSizing: 'border-box',
            }}/>
        </div>

        <div style={{ padding: '10px 20px 6px' }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 9, textTransform: 'uppercase', letterSpacing: 0.4 }}>Photos of the issue</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {attachments.map(p => {
              const on = selectedPhotoIds.includes(p.id)
              return (
                <button key={p.id} type="button"
                  onClick={() => setSelectedPhotoIds(ids => ids.includes(p.id) ? ids.filter(x => x !== p.id) : [...ids, p.id])}
                  style={{
                    position: 'relative', width: 60, height: 60, padding: 0, cursor: 'pointer',
                    borderRadius: 'var(--r-sm)', border: `2px solid ${on ? 'var(--st-error)' : 'var(--border)'}`,
                    overflow: 'hidden', background: 'none',
                  }}>
                  <img src={attachmentThumbUrl(p)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: on ? 1 : 0.55 }}/>
                  {on && (
                    <span style={{
                      position: 'absolute', top: 3, right: 3, width: 16, height: 16, borderRadius: '50%',
                      background: 'var(--st-error)', color: 'var(--st-error-ink)', display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}><Icon name="check" size={9} sw={3}/></span>
                  )}
                </button>
              )
            })}
            <button type="button" onClick={onAttachFile} style={{
              width: 60, height: 60, borderRadius: 'var(--r-sm)', cursor: 'pointer',
              display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
              background: 'var(--brand-gold-soft)', border: '1.5px dashed var(--brand-gold-border)',
              color: 'var(--brand-gold)', fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 600,
            }}>
              <Icon name="paperclip" size={16} sw={2.2}/>Add
            </button>
          </div>
        </div>

        <div style={{ padding: '10px 20px 16px' }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 9, textTransform: 'uppercase', letterSpacing: 0.4 }}>Also notify</div>
          <NotifyPicker options={peopleOptions} value={notify} onChange={setNotify}/>
        </div>

        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 20px',
          borderTop: '1px solid var(--border)', position: 'sticky', bottom: 0, background: 'var(--bg-surface)',
        }}>
          <button type="button" onClick={onClose} style={{
            padding: '0 16px', minHeight: 40, borderRadius: 'var(--r-sm)',
            border: '1px solid var(--border-strong)', background: 'transparent',
            color: 'var(--text-primary)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
          }}>Cancel</button>
          <button type="button" disabled={!reason || submitting} onClick={handleSubmit} style={{
            padding: '0 18px', minHeight: 40, borderRadius: 'var(--r-sm)', border: 'none',
            background: reason ? 'var(--st-error)' : 'var(--bg-elevated)',
            color: reason ? 'var(--st-error-ink)' : 'var(--text-dim)', fontSize: 13.5, fontWeight: 700,
            cursor: reason ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 7,
          }}>
            <Icon name="flag" size={15} sw={2.2}/> {submitting ? 'Flagging…' : 'Flag & block'}
          </button>
        </div>
      </div>
    </div>, document.body)
}

// ── TaskFlagBanner — actionable, resolvable; matches task-peek-web.jsx ──
export function TaskFlagBanner({
  flag, attachments, peopleOptions, canEdit, onResolve, resolving,
}: {
  flag: TaskFlag
  attachments: FlagPhotoOption[]
  peopleOptions: FlagPersonOption[]
  canEdit: boolean
  onResolve: () => void | Promise<void>
  resolving?: boolean
}) {
  const photoIds = new Set((flag.photo_attachment_ids || []).map(String))
  const photos = attachments.filter(a => photoIds.has(a.id))
  const notifyPeople = (flag.notify_user_ids || [])
    .map(id => peopleOptions.find(p => Number(p.id) === id))
    .filter((p): p is FlagPersonOption => !!p)
  const flaggedAt = flag.flagged_at
    ? formatDateWithOptions(flag.flagged_at, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : ''

  return (
    <div style={{
      marginTop: 13, borderRadius: 'var(--r-md)', overflow: 'hidden',
      border: '1px solid color-mix(in srgb, var(--st-error) 35%, transparent)',
      background: 'color-mix(in srgb, var(--st-error) 8%, transparent)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 13px' }}>
        <span style={{ color: 'var(--st-error)', flexShrink: 0, marginTop: 1 }}><Icon name="flag" size={16} sw={2}/></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 700, color: 'var(--st-error)' }}>{flag.reason}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-dim)' }}>
              flagged by {flag.flagged_by_name || 'someone'}{flaggedAt ? ` · ${flaggedAt}` : ''}
            </span>
          </div>
          {flag.note && (
            <p style={{ margin: '4px 0 0', fontFamily: 'var(--font-sans)', fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary)' }}>{flag.note}</p>
          )}
          {photos.length > 0 && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {photos.map(p => (
                <img key={p.id} src={attachmentThumbUrl(p)} alt=""
                  style={{ width: 46, height: 46, borderRadius: 6, border: '1px solid var(--border)', objectFit: 'cover' }}/>
              ))}
            </div>
          )}
          {(flag.notify_user_ids || []).length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 9 }}>
              <AvatarStack
                ids={notifyPeople.length ? notifyPeople.map(p => p.initials ?? p.id) : flag.notify_user_ids.map(String)}
                size={19} max={5}/>
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 11.5, color: 'var(--text-muted)' }}>notified</span>
            </div>
          )}
        </div>
        {canEdit && (
          <button type="button" onClick={onResolve} disabled={resolving} style={{
            flexShrink: 0, padding: '0 12px', minHeight: 32, borderRadius: 'var(--r-sm)',
            border: '1px solid color-mix(in srgb, var(--st-error) 40%, transparent)',
            background: 'var(--bg-surface)', color: 'var(--st-error)',
            fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700,
            cursor: resolving ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
          }}>
            <Icon name="check" size={14} sw={2.4}/> {resolving ? 'Resolving…' : 'Resolve'}
          </button>
        )}
      </div>
    </div>
  )
}
